import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Link } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import * as React from "react";

import { CATEGORY_GRADIENT_CLASSNAME } from "../constants/post";

dayjs.extend(utc);
dayjs.extend(timezone);

export type PostType = Pick<MdxNode, "excerpt" | "fields" | "frontmatter">;

export interface PostProps {
  post: PostType;
  onClick?: () => void;
  titleRenderer?: (
    title: PostType["frontmatter"]["title"],
    post: PostType,
  ) => React.ReactElement;
  excerptRenderer?: (
    excerpt: PostType["excerpt"],
    post: PostType,
  ) => React.ReactElement;
  size?: "normal" | "large";
  showBanner?: boolean;
  dateField?: "date" | "updated";
  className?: string;
  categoryClassName?: string;
  titleClassName?: string;
  excerptClassName?: string;
  footerClassName?: string;
}

/** 博客列表栏的博客简介 */
const Post: React.FC<PostProps> = (props) => {
  const {
    post,
    onClick,
    titleRenderer,
    excerptRenderer,
    size = "normal",
    showBanner: propsShowBanner = false,
    dateField = "date",
    className = "",
    categoryClassName = "",
    titleClassName = "",
    excerptClassName = "",
    footerClassName = "",
  } = props;
  const {
    fields: { slug },
    frontmatter,
    excerpt,
  } = post;
  const {
    banner = null,
    categories = [],
    date: dateString,
    updated: updatedDateString,
    tags = [],
    title,
  } = frontmatter;

  const bannerImage = getImage(banner);
  const showBanner = propsShowBanner && bannerImage;
  const { date, updatedDate } = React.useMemo(() => {
    const date = dayjs.utc(dateString).tz("Asia/Shanghai");
    return {
      date,
      updatedDate: updatedDateString
        ? dayjs.utc(updatedDateString).tz("Asia/Shanghai")
        : date,
    };
  }, [dateString, updatedDateString]);
  const displayedDate = dateField === "updated" ? updatedDate : date;

  const titleDom = (
    <>
      {categories?.length && (
        <div
          className={`w-fit bg-gradient-to-tr from-0% via-40% to-90% bg-clip-text font-bold text-transparent ${size === "large" ? "text-base" : "text-sm"} ${CATEGORY_GRADIENT_CLASSNAME[categories[0]] ?? ""} ${categoryClassName}`}
        >
          {categories[0]}
        </div>
      )}
      <h1
        title={title}
        className={`line-clamp-3 font-bold ${size === "large" ? "text-lg" : "text-base"} ${titleClassName}`}
      >
        {titleRenderer?.(title, post) || title}
      </h1>
    </>
  );

  return (
    <Link
      to={`/posts/${slug}`}
      onClick={onClick}
      className={`item-selectable flex flex-col gap-1.5 rounded-lg px-4 py-3 ${className}`}
    >
      {showBanner ? (
        <div className="relative -mx-4">
          <GatsbyImage
            image={bannerImage}
            alt={title ?? slug}
            className="h-52 w-full rounded-md object-cover object-center md:h-56 xl:h-60"
            loading="lazy"
          />
          <div className="post-banner absolute inset-x-0 bottom-0 flex flex-col gap-0.5 bg-[var(--post-banner-background)] px-4 py-2.5 text-foreground backdrop-blur-sm">
            {titleDom}
          </div>
        </div>
      ) : (
        titleDom
      )}
      {excerpt && (
        <p title={excerpt} className={`${excerptClassName}`}>
          {excerptRenderer?.(excerpt, post) || excerpt}
        </p>
      )}
      <div
        className={`flex text-foreground-secondary ${size === "large" ? "text-base" : "text-sm"} ${footerClassName}`}
      >
        <div
          title={`首次发布于：${date.format("YYYY-MM-DD HH:mm:ss [UTC+8]")}\n最后更新于：${updatedDate.format("YYYY-MM-DD HH:mm:ss [UTC+8]")}`}
          className="line-clamp-1"
        >
          {displayedDate.format(
            size === "large" ? "MM 月 DD 日 YYYY 年" : "MM月DD日YYYY年",
          )}
        </div>
        {tags?.length && (
          <div
            title={tags.join(" ")}
            className={`line-clamp-1 flex-1 before:content-['•'] ${size === "large" ? "before:mx-1.5" : "before:mx-1"}`}
          >
            {tags.join(" ")}
          </div>
        )}
      </div>
    </Link>
  );
};

export default React.memo(Post);
